import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { motion, AnimatePresence } from 'framer-motion';

import {
  createApplicationRole,
  deleteApplicationRole,
  fetchApplicationRoleCatalog,
  fetchApplicationRoles,
  updateApplicationRole,
} from '../../services/applicationRolesApi';
import { getSelectedPlatformTenant, mapLaravelError } from '../../services/platformTenantsApi';

const emptyRole = {
  id: null,
  role_key: '',
  name: '',
  description: '',
  permissions: [],
  workflow_actions: [],
  is_active: true,
  users_count: 0,
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeRoleKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const normalizeRoleLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const dedupeRoles = (items) => {
  const seenKeys = new Set();
  const seenLabels = new Set();

  return asArray(items).filter((role) => {
    const roleKey = normalizeRoleKey(role?.role_key);
    const labelKey = normalizeRoleLabel(role?.name);

    if ((roleKey && seenKeys.has(roleKey)) || (labelKey && seenLabels.has(labelKey))) {
      return false;
    }

    if (roleKey) seenKeys.add(roleKey);
    if (labelKey) seenLabels.add(labelKey);

    return true;
  });
};

const groupByCategory = (items) =>
  asArray(items).reduce((groups, item) => {
    const category = item?.category || 'General';
    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
    return groups;
  }, {});

const toggleKey = (list, key) => {
  const current = new Set(asArray(list));
  if (current.has(key)) current.delete(key);
  else current.add(key);
  return Array.from(current);
};

const pageMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: 'easeOut' },
};

const listMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.18, ease: 'easeOut' },
};

function ApplicationRolesPage() {
  const selectedTenant = getSelectedPlatformTenant();

  const [systemRoles, setSystemRoles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [workflowActions, setWorkflowActions] = useState([]);
  const [form, setForm] = useState(emptyRole);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [roleKeyTouched, setRoleKeyTouched] = useState(false);

  const permissionGroups = useMemo(() => groupByCategory(permissions), [permissions]);
  const workflowGroups = useMemo(() => groupByCategory(workflowActions), [workflowActions]);
  const allRoles = useMemo(() => dedupeRoles([...systemRoles, ...roles]), [roles, systemRoles]);
  const isSystemRole = form?.is_system === true;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [catalogPayload, rolesPayload] = await Promise.all([
        fetchApplicationRoleCatalog(),
        fetchApplicationRoles({ include_inactive: 1 }),
      ]);

      setPermissions(asArray(catalogPayload?.permissions));
      setWorkflowActions(asArray(catalogPayload?.workflow_actions));
      setSystemRoles(asArray(catalogPayload?.system_roles));
      setRoles(asArray(rolesPayload?.roles).map((role) => ({ ...role, is_system: false })));
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
      setSystemRoles([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRoles = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allRoles;

    return allRoles.filter((role) => (
      String(role?.name || '').toLowerCase().includes(needle)
      || String(role?.role_key || '').toLowerCase().includes(needle)
      || String(role?.description || '').toLowerCase().includes(needle)
    ));
  }, [allRoles, search]);

  const selectRole = (role) => {
    setForm({
      ...emptyRole,
      ...role,
      permissions: asArray(role?.permissions),
      workflow_actions: asArray(role?.workflow_actions),
      is_active: role?.is_active !== false,
      is_system: role?.is_system === true,
    });
    setRoleKeyTouched(true);
    setError('');
    setNotice('');
  };

  const startNewRole = () => {
    setForm(emptyRole);
    setRoleKeyTouched(false);
    setError('');
    setNotice('');
  };

  const handleNameChange = (event) => {
    const nextName = event.target.value;
    setForm((current) => ({
      ...current,
      name: nextName,
      role_key: !current.id && !roleKeyTouched ? normalizeRoleKey(nextName) : current.role_key,
    }));
  };

  const handleRoleKeyChange = (event) => {
    setRoleKeyTouched(true);
    setForm((current) => ({
      ...current,
      role_key: normalizeRoleKey(event.target.value),
    }));
  };

  const handlePermissionToggle = (key) => {
    setForm((current) => ({
      ...current,
      permissions: toggleKey(current.permissions, key),
    }));
  };

  const handleWorkflowToggle = (key) => {
    setForm((current) => ({
      ...current,
      workflow_actions: toggleKey(current.workflow_actions, key),
    }));
  };

  const validateForm = () => {
    if (!form.name.trim()) return 'Role name is required.';
    if (!form.role_key.trim()) return 'Role key is required.';
    if (!/^[a-z][a-z0-9_]*$/.test(form.role_key)) return 'Role key must start with a letter and use lowercase letters, numbers, or underscores.';
    return '';
  };

  const handleSave = async () => {
    if (isSystemRole) {
      setError('System roles are read-only. Create a custom role to change permissions.');
      setNotice('');
      return;
    }

    const validationMessage = validateForm();
    if (validationMessage) {
      setError(validationMessage);
      setNotice('');
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');

    const payload = {
      role_key: form.role_key,
      name: form.name,
      description: form.description,
      permissions: asArray(form.permissions),
      workflow_actions: asArray(form.workflow_actions),
      is_active: form.is_active !== false,
    };

    try {
      const response = form.id
        ? await updateApplicationRole(form.id, payload)
        : await createApplicationRole(payload);

      const savedRole = response?.role;
      setNotice(response?.message || 'Role saved.');
      await loadData();
      if (savedRole) selectRole(savedRole);
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form.id || isSystemRole) return;
    if (!window.confirm(`Delete role "${form.name}"? This is only allowed when no users are assigned to it.`)) return;

    setDeleting(true);
    setError('');
    setNotice('');

    try {
      const response = await deleteApplicationRole(form.id);
      setNotice(response?.message || 'Role deleted.');
      startNewRole();
      await loadData();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setDeleting(false);
    }
  };

  const roleMetrics = useMemo(() => {
    const activeRoles = allRoles.filter((role) => role?.is_active !== false).length;
    const assignedUsers = allRoles.reduce((total, role) => total + Number(role?.users_count || 0), 0);

    return [
      { label: 'Roles', value: allRoles.length, icon: <AdminPanelSettingsOutlinedIcon fontSize="small" /> },
      { label: 'Active', value: activeRoles, icon: <CheckCircleOutlineIcon fontSize="small" /> },
      { label: 'Assigned users', value: assignedUsers, icon: <GroupOutlinedIcon fontSize="small" /> },
      { label: 'Selected grants', value: asArray(form.permissions).length + asArray(form.workflow_actions).length, icon: <VpnKeyOutlinedIcon fontSize="small" /> },
    ];
  }, [allRoles, form.permissions, form.workflow_actions]);

  const renderToggleGroups = (groups, selectedKeys, onToggle, readOnly = false) => (
    <Stack spacing={1.5}>
      {Object.entries(groups).map(([category, items]) => (
        <Box key={category}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: 0.8 }}>
              {category}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>

          <Grid container spacing={1}>
            {items.map((item) => {
              const checked = asArray(selectedKeys).includes(item.key);

              return (
                <Grid size={{ xs: 12, md: 6 }} key={item.key}>
                  <ButtonBase
                    disabled={readOnly}
                    onClick={() => {
                      if (!readOnly) onToggle(item.key);
                    }}
                    focusRipple
                    sx={{
                      width: '100%',
                      minHeight: 74,
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                      border: '1px solid',
                      borderColor: checked ? 'success.main' : 'divider',
                      borderRadius: 1.5,
                      px: 1.35,
                      py: 1.1,
                      textAlign: 'left',
                      cursor: readOnly ? 'default' : 'pointer',
                      bgcolor: checked ? (theme) => alpha(theme.palette.success.main, 0.08) : 'background.paper',
                      transition: 'background-color 180ms ease, border-color 180ms ease',
                      '&.Mui-disabled': {
                        color: 'inherit',
                        opacity: 1,
                      },
                      '&:hover': {
                        borderColor: checked ? 'success.main' : 'text.secondary',
                        bgcolor: checked ? (theme) => alpha(theme.palette.success.main, 0.12) : 'action.hover',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                      <Box sx={{ color: checked ? 'success.main' : 'text.disabled', pt: 0.1 }}>
                        {checked ? <CheckCircleOutlineIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                      </Box>
                      <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                          {item.label}
                        </Typography>
                        {item.description ? (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                            {item.description}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Box
      component={motion.div}
      {...pageMotion}
      sx={{
        minHeight: '100%',
        p: { xs: 1.5, md: 3 },
        bgcolor: '#f7f8fb',
      }}
    >
      <Stack spacing={2.25}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={{ xs: 2, lg: 3 }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'center' }}
            sx={{ p: { xs: 2, md: 2.5 } }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  icon={<AdminPanelSettingsOutlinedIcon />}
                  label="Super admin"
                  sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '& .MuiChip-icon': { color: 'inherit' } }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={selectedTenant?.key ? selectedTenant.name || selectedTenant.key : 'Login tenant'}
                />
              </Stack>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#1E1B4B', letterSpacing: 0 }}>
                  Role Permissions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                  Shape each tenant role with only the access and workflow actions it needs.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
                Refresh
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={startNewRole} sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#0f9f72' } }}>
                New Role
              </Button>
            </Stack>
          </Stack>

          <Divider />

          <Grid container>
            {roleMetrics.map((metric, index) => (
              <Grid size={{ xs: 6, md: 3 }} key={metric.label}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 1.6,
                    borderRight: { md: index === roleMetrics.length - 1 ? 0 : '1px solid' },
                    borderBottom: { xs: index < 2 ? '1px solid' : 0, md: 0 },
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>{metric.icon}</Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                      {metric.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {metric.label}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>

        {selectedTenant?.key ? (
          <Alert severity="info" sx={{ borderRadius: 1.5 }}>
            Managing the selected tenant: {selectedTenant.name || selectedTenant.key}.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            No tenant is selected. Changes apply to the current login tenant.
          </Alert>
        )}

        {error ? <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 1.5 }}>{error}</Alert> : null}
        {notice ? <Alert severity="success" onClose={() => setNotice('')} sx={{ borderRadius: 1.5 }}>{notice}</Alert> : null}

        <Grid container spacing={2.25} alignItems="stretch">
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box
              sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Stack spacing={1.5} sx={{ p: 1.75 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack spacing={0.15}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Role Library
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filteredRoles.length} visible
                    </Typography>
                  </Stack>
                  {loading ? <CircularProgress size={22} /> : null}
                </Stack>

                <TextField
                  placeholder="Search roles"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Divider />

              <Box sx={{ maxHeight: { xs: 360, lg: 720 }, overflowY: 'auto', p: 1 }}>
                {loading ? (
                  <Stack spacing={1} alignItems="center" sx={{ py: 5 }}>
                    <CircularProgress size={26} />
                    <Typography variant="body2" color="text.secondary">
                      Loading roles...
                    </Typography>
                  </Stack>
                ) : filteredRoles.length === 0 ? (
                  <Stack spacing={0.5} sx={{ px: 1, py: 4 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      No roles found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Create a new role or adjust the search term.
                    </Typography>
                  </Stack>
                ) : (
                  <AnimatePresence initial={false}>
                    {filteredRoles.map((role) => {
                      const selected = String(form.id || '') === String(role.id);

                      return (
                        <Box
                          key={`${role.is_system ? 'system' : 'custom'}:${role.role_key || role.id}`}
                          component={motion.div}
                          {...listMotion}
                          layout
                          sx={{ mb: 0.75 }}
                        >
                          <ButtonBase
                            onClick={() => selectRole(role)}
                            sx={{
                              width: '100%',
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: selected ? 'primary.main' : 'transparent',
                              bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                              px: 1.25,
                              py: 1.15,
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              transition: 'background-color 180ms ease, border-color 180ms ease',
                              '&:hover': {
                                bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'action.hover',
                              },
                            }}
                          >
                            <Stack spacing={0.8} sx={{ width: '100%', minWidth: 0 }}>
                              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                                    {role.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {role.role_key}
                                  </Typography>
                                </Box>
                                {role.is_system ? (
                                  <Chip
                                    size="small"
                                    label="System"
                                    color="primary"
                                    variant="filled"
                                    sx={{ height: 22 }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label={role.is_active ? 'Active' : 'Off'}
                                    color={role.is_active ? 'success' : 'default'}
                                    variant={role.is_active ? 'filled' : 'outlined'}
                                    sx={{ height: 22 }}
                                  />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" variant="outlined" icon={<VpnKeyOutlinedIcon />} label={`${asArray(role.permissions).length} permissions`} />
                                <Chip size="small" variant="outlined" icon={<GroupOutlinedIcon />} label={`${role.users_count || 0} users`} />
                              </Stack>
                            </Stack>
                          </ButtonBase>
                        </Box>
                      );
                    })}
                  </AnimatePresence>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 8 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ p: { xs: 2, md: 2.5 } }}
              >
                <Stack spacing={0.35}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900, letterSpacing: 0.8 }}>
                      {isSystemRole ? 'System role' : form.id ? 'Edit role' : 'Create role'}
                    </Typography>
                    {isSystemRole ? <Chip size="small" label="Read only" variant="outlined" color="primary" /> : null}
                  </Stack>
                  <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: 0 }}>
                    {form.name || 'Untitled role'}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {form.id && !isSystemRole ? (
                    <Tooltip title={Number(form.users_count || 0) > 0 ? 'Assigned roles cannot be deleted' : 'Delete role'}>
                      <span>
                        <IconButton
                          color="error"
                          onClick={handleDelete}
                          disabled={deleting || Number(form.users_count || 0) > 0}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  {isSystemRole ? (
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={startNewRole}>
                      New custom role
                    </Button>
                  ) : (
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Divider />

              <Stack spacing={2.25} sx={{ p: { xs: 2, md: 2.5 } }}>
                {isSystemRole ? (
                  <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                    System roles are read-only defaults. Create a custom role when a tenant needs a different permission set.
                  </Alert>
                ) : null}

                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Role name"
                      value={form.name}
                      onChange={handleNameChange}
                      disabled={isSystemRole}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Role key"
                      value={form.role_key}
                      onChange={handleRoleKeyChange}
                      disabled={isSystemRole}
                      required
                      fullWidth
                      size="small"
                      helperText="Lowercase letters, numbers, underscores."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Description"
                      value={form.description || ''}
                      onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                      disabled={isSystemRole}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1,
                        bgcolor: form.is_active !== false ? (theme) => alpha(theme.palette.success.main, 0.06) : 'background.default',
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            checked={form.is_active !== false}
                            disabled={isSystemRole}
                            onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))}
                          />
                        }
                        label={
                          <Stack spacing={0.1}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              Active and assignable
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Inactive roles remain saved but cannot be chosen for new assignments.
                            </Typography>
                          </Stack>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Divider />

                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Application permissions
                    </Typography>
                    <Chip size="small" label={`${asArray(form.permissions).length} selected`} color={asArray(form.permissions).length ? 'primary' : 'default'} />
                  </Stack>
                  {renderToggleGroups(permissionGroups, form.permissions, handlePermissionToggle, isSystemRole)}
                </Stack>

                <Divider />

                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Workflow actions
                    </Typography>
                    <Chip size="small" label={`${asArray(form.workflow_actions).length} selected`} color={asArray(form.workflow_actions).length ? 'success' : 'default'} />
                  </Stack>
                  {renderToggleGroups(workflowGroups, form.workflow_actions, handleWorkflowToggle, isSystemRole)}
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}

export default ApplicationRolesPage;
